import { useCallback } from "react";
import { FormatDateOptions, useIntl } from "react-intl";
import { Grid, Tooltip, Typography } from "@mui/material";
import { Device, FirmwareUpgradeState } from "../utils/types";

import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import StaticRefreshControl from "./StaticRefreshControl";

const dateFormatOptions: FormatDateOptions = {
  dateStyle: "short",
  timeStyle: "medium",
};

export const useUpdateStatusLabel = () => {
  const { formatMessage } = useIntl();
  const updateStatusLabel = useCallback(
    (status: number) => {
      switch (status) {
        case 0:
          return formatMessage({ defaultMessage: "new" });
        case 1:
          return formatMessage({ defaultMessage: "in progress" });
        case 2:
          return formatMessage({ defaultMessage: "retry" });
        case 3:
          return formatMessage({ defaultMessage: "failed" });
        case 4:
          return formatMessage({ defaultMessage: "success" });
        default:
          return null;
      }
    },
    [formatMessage]
  );

  return { updateStatusLabel };
};

export const FirmwareUpdateDeviceItem = ({
  device: { unitid, unitDetails },
  status,
  refreshStatus,
}: {
  device: Device;
  status: FirmwareUpgradeState | null;
  refreshStatus: (unitid: number) => {};
}) => {
  const { formatMessage, formatDate } = useIntl();
  const { updateStatusLabel } = useUpdateStatusLabel();

  const callRefreshStatus = useCallback(() => refreshStatus(unitid), [refreshStatus, unitid]);

  return (
    <Grid container spacing={1} sx={{ flexDirection: "row", alignItems: "center" }}>
      {status && status.upgrade_status < 3 && (
        <Grid item>
          <Tooltip
            title={formatMessage({
              defaultMessage: "Refresh update state",
            })}
          >
            <StaticRefreshControl onRefresh={callRefreshStatus} value={10} disableSelection color="info.light" />
          </Tooltip>
        </Grid>
      )}
      {status && status.upgrade_status === 4 && (
        <Grid item>
          <CheckIcon sx={{ color: (theme) => theme.palette.success.light, m: 1 }} />
        </Grid>
      )}
      {status && status.upgrade_status === 3 && (
        <Grid item>
          <ClearIcon sx={{ color: (theme) => theme.palette.error.light, m: 1 }} />
        </Grid>
      )}
      <Grid item>
        <Grid container spacing={0}>
          <Grid item xs={12} sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "flex-end" }}>
            <Typography>{unitid}</Typography>
            <Typography variant="caption" sx={{ ml: 0.5 }}>
              {unitDetails?.company_name}
            </Typography>
          </Grid>
          {!status && (
            <Grid item xs={12}>
              <Typography variant="caption">
                {formatMessage({ defaultMessage: "Software version" })}: {unitDetails?.swver}
              </Typography>
            </Grid>
          )}
          {status && (
            <Grid item xs={12}>
              <Typography variant="caption">
                {formatMessage({ defaultMessage: "Status:" })} {updateStatusLabel(status.upgrade_status)} (
                {formatDate(status.upgrade_status_ts ?? status.upgrade_ts, dateFormatOptions)})
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
